import React, { useMemo } from "react";
import { InvoiceSource } from "@common/types/invoice";
import { CATALOG_ITEM_ACCOUNT_SOURCES } from "@common/types/catalogItem";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import {
    InstitutionIcon,
    type InstitutionKey,
} from "@design-system/InstitutionIcon/InstitutionIcon";
import { ImportedStatementIcon } from "@icons";

// "Kick" is the native (MANUAL) account; the rest map to their invoice
// integration sources. MCP is never offered as a selectable account.
const ACCOUNT_SOURCE_LABELS: Record<InvoiceSource, string> = {
    [InvoiceSource.MANUAL]: "Kick",
    [InvoiceSource.STRIPE]: "Stripe",
    [InvoiceSource.BILLCOM]: "Bill",
    [InvoiceSource.MERCURY]: "Mercury",
    [InvoiceSource.IMPORTER]: "Imported",
    [InvoiceSource.MCP]: "Agent",
    [InvoiceSource.QUICKBOOKS]: "QuickBooks",
};

const ACCOUNT_SOURCE_INSTITUTIONS: Partial<
    Record<InvoiceSource, InstitutionKey>
> = {
    [InvoiceSource.MANUAL]: "kick",
    [InvoiceSource.STRIPE]: "stripe",
    [InvoiceSource.MERCURY]: "mercury",
    [InvoiceSource.BILLCOM]: "bill",
    [InvoiceSource.QUICKBOOKS]: "quickbooks",
};

const AccountSourceIcon: React.FC<{ source: InvoiceSource }> = ({ source }) => {
    const institution = ACCOUNT_SOURCE_INSTITUTIONS[source];
    if (institution) {
        return <InstitutionIcon institution={institution} size="xs" />;
    }
    return source === InvoiceSource.IMPORTER ? <ImportedStatementIcon /> : null;
};

const ACCOUNT_SOURCE_ITEMS: Array<PickerItemData<InvoiceSource>> =
    CATALOG_ITEM_ACCOUNT_SOURCES.map((source) => ({
        value: source,
        label: ACCOUNT_SOURCE_LABELS[source],
        leading: <AccountSourceIcon source={source} />,
    }));

interface Props {
    sources: InvoiceSource[];
    onChange: (sources: InvoiceSource[]) => void;
}

export const CatalogItemAccountFilter: React.FC<Props> = ({
    sources,
    onChange,
}) => {
    const triggerLabel = useMemo(() => {
        if (
            sources.length === 0 ||
            sources.length === CATALOG_ITEM_ACCOUNT_SOURCES.length
        ) {
            return "All accounts";
        }
        if (sources.length === 1) {
            return ACCOUNT_SOURCE_LABELS[sources[0]];
        }
        return `${sources.length} accounts`;
    }, [sources]);

    return (
        <Picker.Select
            multiple
            items={ACCOUNT_SOURCE_ITEMS}
            value={sources}
            onValueChange={onChange}
            placeholder={triggerLabel}
            renderValue={() => triggerLabel}
            chevron="none"
        />
    );
};
